import React, { useCallback, useEffect, useState } from 'react';
import { ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { trackEvent } from 'utils/analytics';
import { propertyInsightsClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { InsightsPeriod, PeakRentalSummary } from 'types/PropertyInsights';
import { SpotInterface } from 'types';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import barChartImg from 'assets/barChart.png';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import { getPeakTimesByDayOfWeekGraphData, getPeakTimesByHourGraphData, insightsPeriodOptions } from '../helpers';
import PeakTimesSkeleton from './PeakTimesSkeleton';
import styles from './PeakTimesWidget.module.css';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';

type PeakTimesWidgetProps = {
    spot: SpotInterface;
    filters: {
        peakTimesPeriod: InsightsPeriod;
    };
    urlUpdater: UrlUpdaterFunctions<{ peakTimesPeriod: InsightsPeriod }>;
};

const options: ChartOptions<'bar'> = {
    interaction: {
        mode: 'index',
        intersect: false,
    },
    scales: {
        y: {
            grid: { display: true, borderDash: [2] },
            ticks: { maxTicksLimit: 4 },
            title: {
                display: true,
                text: 'Rentals',
            },
        },
    },
};

type PeakTimesGraphData = { data: number[]; labels: string[]; graphColors: string[] };

function PeakTimesWidget({ spot, filters, urlUpdater }: PeakTimesWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [summary, setSummary] = useState<PeakRentalSummary | null>(null);
    const [byHourGraphData, setByHourGraphData] = useState<PeakTimesGraphData | null>(null);
    const [byDayOfWeekGraphData, setByDayOfWeekGraphData] = useState<PeakTimesGraphData | null>(null);

    const getPeakTimes = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const [summary, byHour, byDayOfWeek] = await Promise.all([
                propertyInsightsClient.getPeakRentalsSummary(spot.uuid, filters.peakTimesPeriod),
                propertyInsightsClient.getPeakRentalsByHour(spot.uuid, filters.peakTimesPeriod),
                propertyInsightsClient.getPeakRentalsByDayOfWeek(spot.uuid, filters.peakTimesPeriod),
            ]);
            setSummary(summary);
            setByHourGraphData(getPeakTimesByHourGraphData(byHour, spot));
            setByDayOfWeekGraphData(getPeakTimesByDayOfWeekGraphData(byDayOfWeek, spot));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.peakTimesPeriod]);

    useEffect(() => {
        getPeakTimes();
    }, [spot.uuid, getPeakTimes, filters.peakTimesPeriod]);

    function handlePeakTimesPeriodSelection(selection: string) {
        urlUpdater.peakTimesPeriod(selection);
        trackEvent('Changed Peak Times Period', {
            spot: spot.pk,
            spotName: spot.name,
            period: selection,
        });
    }

    const isLoading = loading || !summary || !byHourGraphData || !byDayOfWeekGraphData;
    const isEmpty = byHourGraphData?.data.length === 0 || byDayOfWeekGraphData?.data.length === 0;

    return (
        <WidgetBox
            title="Peak Rental Times"
            rightElement={
                <AGSimpleSelect
                    options={insightsPeriodOptions}
                    selected={filters.peakTimesPeriod}
                    onSelect={handlePeakTimesPeriodSelection}
                    width="160px"
                />
            }
        >
            {isEmpty && !isLoading ? (
                <EmptyWidget imgUrl={barChartImg} />
            ) : errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getPeakTimes} />
            ) : (
                <div>
                    {isLoading ? (
                        <PeakTimesSkeleton />
                    ) : (
                        <div className={styles.peakTimesSummaryContainer}>
                            <div className={styles.peakTimesSummary}>
                                <div>
                                    <p>Busiest Day</p>
                                    <h4>{summary.busiest_day}</h4>
                                </div>
                                <div>
                                    <p>Busiest Hour</p>
                                    <h4>{summary.busiest_hour}</h4>
                                </div>
                            </div>

                            <div>
                                <p className={styles.peakTimeSubtitle}>Peak Day</p>
                                <Bar
                                    data={{
                                        labels: byDayOfWeekGraphData.labels,
                                        datasets: [
                                            {
                                                data: byDayOfWeekGraphData.data,
                                                backgroundColor: byDayOfWeekGraphData.graphColors,
                                            },
                                        ],
                                    }}
                                    options={options}
                                />
                            </div>
                            <div>
                                <p className={styles.peakTimeSubtitle}>Peak Time</p>
                                <Bar
                                    data={{
                                        labels: byHourGraphData.labels,
                                        datasets: [
                                            {
                                                data: byHourGraphData.data,
                                                backgroundColor: byHourGraphData.graphColors,
                                            },
                                        ],
                                    }}
                                    options={options}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </WidgetBox>
    );
}

export default PeakTimesWidget;
